import { Autocomplete } from "@mui/lab";
import { CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { ErrorModal } from "../../components/modals/error-modal";
import { ShiftInfo } from "../shift-navigator";
import { getShifts } from "./repositories/ShiftRepository";

type Props = {
	label?: string;
	value: number | null;
	onChange?: (id: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
};

export const useShifts = (shiftId: number | null) => {
	return useQuery<ShiftInfo[], Error>({
		queryKey: ["shifts", shiftId],
		queryFn: ({ signal }) => getShifts({ shiftId }, signal),
	});
};

export const ShiftSelector = ({
	label = "Shift - Crew",
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: shifts, isLoading, isError, error: e } = useShifts(value);

	const selectedShift = shifts?.find((s) => s.CurrentShiftId === value) || null;

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={shifts || []}
				getOptionLabel={(option) =>
					`${option.CurrentShift} - ${option.CurrentCrew}`
				}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>{params.InputProps.endAdornment}</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedShift}
				onChange={(event, newValue) => {
					onChange(newValue?.CurrentShiftId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading shifts"
			/>
		</>
	);
};
